// BadgeImage Component
// Renders badge images from Supabase Storage with fallback support
// Uses useBadgeAssets hook to get resolved CDN URLs

import { useState } from 'react';
import { Trophy, Target, Flame, Star, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export type BadgeCategory = 'analysis' | 'practice' | 'streak' | 'milestone' | 'social';
export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';

interface BadgeImageProps {
  category: BadgeCategory;
  rarity: BadgeRarity;
  badge_key?: string;
  achievement_id?: string; // Badge ID for URL lookup
  image_path?: string | null; // Backward compatibility - deprecated
  size?: number;
  className?: string;
}

// Map category to icon name for path construction
const CATEGORY_TO_ICON: Record<BadgeCategory, string> = {
  analysis: 'Target',
  practice: 'Trophy',
  streak: 'Flame',
  milestone: 'Star',
  social: 'Community',
};

// Map rarity to capitalized name
const RARITY_CAPITALIZED: Record<BadgeRarity, string> = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

// Fallback icon component mapping
// Using LucideIcon type to support size prop
import type { LucideIcon } from 'lucide-react';

const CATEGORY_ICON_COMPONENT: Record<BadgeCategory, LucideIcon> = {
  analysis: Target,
  practice: Trophy,
  streak: Flame,
  milestone: Star,
  social: Users,
};

export function BadgeImage({
  category,
  rarity,
  badge_key,
  achievement_id,
  image_path, // Backward compatibility - deprecated
  size = 64,
  className = '',
}: BadgeImageProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Only fetch badge assets as absolute last resort - we can construct URLs from category/achievement_id
  // Only call edge function if we ONLY have badge_key and nothing else (no icon_path, no achievement_id)
  // Even then, we'll try to construct a fallback URL first before using edge function results
  const needsLookup = !image_path && !achievement_id && !!badge_key;
  // Archived: useBadgeAssets moved to archive/community (clubhouse feature retired)
  // const { data: badges, isLoading: badgesLoading } = useBadgeAssets({
  const badges = null;
  const badgesLoading = false;

  // Get badge URL - prioritize direct construction over edge function lookup
  const getBadgeImageUrl = (): string | null => {
    // Priority 1: Use image_path if provided (most efficient)
    if (image_path) {
      // If it's already a full URL, use it
      if (image_path.startsWith('http')) {
        return image_path;
      }
      
      // Use Supabase client to get public URL (handles URL construction properly)
      try {
        // Extract the path after 'badges/' if it starts with 'badges/'
        let storagePath = image_path;
        if (image_path.startsWith('badges/')) {
          // Remove 'badges/' prefix - getPublicUrl expects path within bucket
          storagePath = image_path.replace(/^badges\//, '');
        } else if (image_path.startsWith('/lovable-uploads/')) {
          // Old format: keep for backward compatibility during migration
          return image_path;
        }
        
        // Use Supabase storage client to get public URL
        const { data } = supabase.storage.from('badges').getPublicUrl(storagePath);
        if (data?.publicUrl) {
          console.log('[BadgeImage] Constructed URL from icon_path:', { 
            image_path, 
            storagePath, 
            url: data.publicUrl,
            bucket: 'badges'
          });
          return data.publicUrl;
        } else {
          console.warn('[BadgeImage] getPublicUrl returned no data:', { image_path, storagePath });
        }
      } catch (error) {
        console.error('[BadgeImage] Error constructing URL from image_path:', error, { image_path });
      }
    }
    
    // Priority 2: Construct URL from category + achievement_id (no edge function needed)
    if (achievement_id) {
      try {
        // Construct path: {category}/{id}.webp
        const storagePath = `${category}/${achievement_id}.webp`;
        const { data } = supabase.storage.from('badges').getPublicUrl(storagePath);
        if (data?.publicUrl) {
          console.log('[BadgeImage] Constructed URL from category/achievement_id:', { 
            category, 
            achievement_id, 
            storagePath,
            url: data.publicUrl 
          });
          return data.publicUrl;
        } else {
          console.warn('[BadgeImage] getPublicUrl returned no data for category/achievement_id:', { category, achievement_id, storagePath });
        }
      } catch (error) {
        console.error('[BadgeImage] Error constructing URL from category/achievement_id:', error, { category, achievement_id });
      }
    }
    
    // Priority 3: Look up by achievement_id from edge function (only if we don't have category/achievement_id)
    if (achievement_id && badges) {
      const url = getBadgeUrl(achievement_id, badges);
      if (url) return url;
    }
    
    // Priority 4: Look up by badge_key from edge function (fallback)
    if (badge_key && badges) {
      // Archived: getBadgeUrlByKey moved to archive/community
      // const url = getBadgeUrlByKey(badge_key, badges);
      // if (url) return url;
    }
    
    return null;
  };

  const imageSrc = getBadgeImageUrl();
  const FallbackIcon = CATEGORY_ICON_COMPONENT[category];

  // Note: badgesLoading is always false since we disabled the hook
  // No loading state needed - we construct URLs directly

  // Show fallback if no image URL available or image failed to load
  if (!imageSrc || imageError) {
    if (import.meta.env.DEV && !imageSrc) {
      console.debug('[BadgeImage] No image URL available, using fallback', { 
        achievement_id,
        badge_key,
        category, 
        rarity
      });
    }
    return (
      <div
        className={`flex items-center justify-center rounded-full bg-muted ${className}`}
        style={{ width: size, height: size }}
        title={imageError ? `Failed to load badge image` : 'Badge image not available'}
      >
        <FallbackIcon className="text-muted-foreground" size={size * 0.6} />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <img
        src={imageSrc}
        alt={`${rarity} ${category} badge${badge_key ? `: ${badge_key}` : ''}`}
        width={size}
        height={size}
        className={`rounded-full object-contain transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => {
          setImageLoaded(true);
        }}
        onError={(e) => {
          // Log error details for debugging
          console.error('[BadgeImage] Image load failed', { 
            imageSrc,
            category, 
            rarity,
            achievement_id,
            badge_key,
            icon_path: image_path,
            error: e,
            note: 'Badge image not found - using fallback icon. This is normal if badge images are not yet uploaded.'
          });
          setImageError(true);
        }}
        style={{ width: size, height: size }}
      />
      {!imageLoaded && !imageError && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-full bg-muted animate-pulse"
          style={{ width: size, height: size }}
        >
          <FallbackIcon className="text-muted-foreground" size={size * 0.4} />
        </div>
      )}
    </div>
  );
}

